import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtDate, normalizeStatus, STATUS_LABELS } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CustomerPicker from "@/components/CustomerPicker";
import DateInput from "@/components/DateInput";

const emptyForm = {
  customer_id: "", job_type: "laser", material: "", dimensions: "", quantity: 1,
  artwork_notes: "", notes: "", deadline: "", status: "new",
  artwork_file_path: null, artwork_file_name: null,
};

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "in_production", label: "In Production" },
  { value: "fulfilled", label: "Fulfilled" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
  { value: "voided", label: "Voided" },
];

export default function WorkOrders() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [tab, setTab] = useState("all");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    let url = "/work-orders";
    if (tab === "archived") url = "/work-orders?archived=true";
    else if (tab === "voided") url = "/work-orders?voided=true";
    return api.get(url).then((r) => setList(r.data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  useEffect(() => {
    // Support ?customer=XX&new=1 deep link from CustomerDetail
    const c = params.get("customer");
    const isNew = params.get("new");
    if (isNew && c) {
      setForm({ ...emptyForm, customer_id: c });
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => (tab === "all" || tab === "archived" || tab === "voided") ? list : list.filter((w) => normalizeStatus(w.status) === tab), [list, tab]);

  const uploadArtwork = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, artwork_file_path: data.path, artwork_file_name: data.filename }));
      toast.success("Artwork uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.customer_id) { toast.error("Choose a customer"); return; }
    setSaving(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity) || 1, deadline: form.deadline || null };
      const { data } = await api.post("/work-orders", payload);
      toast.success(`Created ${data.number}`);
      setOpen(false); setForm(emptyForm); load();
      navigate(`/work-orders/${data.id}`);
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader
        title="Work Orders"
        subtitle={`${list.length} total · auto-generated from invoices`}
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="bg-transparent border hairline p-1 rounded-sm h-auto">
          {STATUS_TABS.map((s) => (
            <TabsTrigger key={s.value} value={s.value} data-testid={`wo-tab-${s.value}`}
              className="rounded-sm text-xs uppercase tracking-wider font-mono data-[state=active]:bg-emerald-700 data-[state=active]:text-white">
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="surface overflow-x-auto fade-up" data-testid="work-orders-table">
        <table className="data-table">
          <thead>
            <tr><th>Number</th><th>Customer</th><th>Type</th><th>Material</th><th>Qty</th><th>Status</th><th>Deadline</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-10">No work orders in this view.</td></tr>
            )}
            {filtered.map((w) => (
              <tr key={w.id} onClick={() => navigate(`/work-orders/${w.id}`)} data-testid={`wo-row-${w.id}`}>
                <td className="font-mono text-slate-900 font-medium">{w.number}</td>
                <td>{w.customer_name || "—"}</td>
                <td><span className={`chip ${w.job_type}`}>{w.job_type}</span></td>
                <td className="text-slate-700">{w.material || "—"}</td>
                <td className="font-mono">{w.quantity}</td>
                <td><span className={`chip ${normalizeStatus(w.status)}`}>{STATUS_LABELS[normalizeStatus(w.status)] || w.status}</span></td>
                <td className="font-mono">{fmtDate(w.deadline)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wo-dialog">
          <DialogHeader><DialogTitle>New Work Order</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Customer *</Label>
              <CustomerPicker
                customers={customers}
                value={form.customer_id}
                onChange={(v) => setForm({ ...form, customer_id: v })}
                onCustomersChange={setCustomers}
              />
            </div>

            <div>
              <Label className="text-xs text-slate-600">Job Type *</Label>
              <Select value={form.job_type} onValueChange={(v) => setForm({ ...form, job_type: v })}>
                <SelectTrigger data-testid="wo-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="laser">Laser Engraving</SelectItem>
                  <SelectItem value="uv">UV Flatbed Printing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_production">In Production</SelectItem>
                  <SelectItem value="fulfilled">Fulfilled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-600">Material</Label>
              <Input data-testid="wo-material" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} placeholder="Wood, Acrylic, Aluminum…" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Dimensions</Label>
              <Input data-testid="wo-dimensions" value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} placeholder='e.g. 12" x 6"' />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Quantity</Label>
              <Input type="number" min="1" data-testid="wo-quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Deadline</Label>
              <DateInput data-testid="wo-deadline" value={form.deadline || ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Artwork / Design Notes</Label>
              <Textarea data-testid="wo-artwork-notes" rows={2} value={form.artwork_notes} onChange={(e) => setForm({ ...form, artwork_notes: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Internal Notes</Label>
              <Textarea data-testid="wo-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Artwork File</Label>
              <div className="border border-dashed hairline p-4 flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  {form.artwork_file_name ? <span className="font-mono">{form.artwork_file_name}</span> : "Attach an image or PDF of the artwork"}
                </div>
                <label className="btn-ghost cursor-pointer text-sm">
                  {uploading ? "Uploading…" : "Choose file"}
                  <input data-testid="wo-artwork-upload" type="file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadArtwork(e.target.files[0])} />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={saving} data-testid="save-work-order-btn">
              {saving ? "Creating…" : "Create Work Order"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
